// src/pages/Orders/PaymentsPage.jsx
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { ROLES } from '../../constants/roles';
import paymentService from '../../services/payment.service';
import Toast from '../../components/Toast/Toast';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import './PaymentsPage.css';

const PAYMENT_MODES = [
  { value: 'CASH',   label: '💵 Cash' },
  { value: 'CHEQUE', label: '📄 Cheque' },
  { value: 'UPI',    label: '📱 UPI' },
  { value: 'NEFT',   label: '🏦 NEFT' },
  { value: 'RTGS',   label: '🏦 RTGS' },
  { value: 'IMPS',   label: '🏦 IMPS' },
];

const STATUS_COLORS = {
  PENDING_PAYMENT: { bg: '#fff8e1', color: '#f57f17' },
  UNDER_REVIEW:    { bg: '#e3f2fd', color: '#1565c0' },
  CONFIRMED:       { bg: '#e8f5e9', color: '#2e7d32' },
  REJECTED:        { bg: '#fce4ec', color: '#c62828' },
};

const STATUS_LABELS = {
  PENDING_PAYMENT: 'Pending',
  UNDER_REVIEW:    'Under Review',
  CONFIRMED:       'Confirmed',
  REJECTED:        'Rejected',
};

const TABS = [
  { key: 'PENDING_PAYMENT', label: '⏳ Pending',     status: 'PENDING_PAYMENT' },
  { key: 'UNDER_REVIEW',    label: '🔍 Review',      status: 'UNDER_REVIEW' },
  { key: 'CONFIRMED',       label: '✅ Received',     status: 'CONFIRMED' },
  { key: 'REJECTED',        label: '❌ Rejected',     status: 'REJECTED' },
  { key: 'DEALER',          label: '🏪 By Dealer',   status: null },
];

// Roles that can add payment entries
const CAN_ADD_ROLES = [
  ROLES.SALES_OFFICER, ROLES.EXECUTIVE, ROLES.AREA_MANAGER,
  ROLES.ZONAL_MANAGER, ROLES.ADMIN, ROLES.OFFICE_ADMIN, ROLES.SUPER_ADMIN,
];
// Roles that can confirm/reject
const CAN_CONFIRM_ROLES = [
  ROLES.ACCOUNTS, ROLES.ADMIN, ROLES.OFFICE_ADMIN, ROLES.SUPER_ADMIN,
];

const fmt       = (a) => `₹${Number(a||0).toLocaleString('en-IN', { minimumFractionDigits:2 })}`;
const fmtDt     = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const modeLabel = (v) => PAYMENT_MODES.find(m => m.value === v)?.label || v || '—';

// ── Add Payment Modal ─────────────────────────────────────────────────────────
const AddPaymentModal = ({ payment, onClose, onSubmit, isSubmitting }) => {
  const totalDue      = parseFloat(payment.amount || 0);
  const totalConfirmed= parseFloat(payment.totalConfirmed || 0);
  const remaining     = totalDue - totalConfirmed;

  const [formData, setFormData] = useState({
    orderId: String(payment.rawOrderId || payment.orderId),
    paidAmount:      String(remaining),
    paymentMode:     '',
    paymentDate:     new Date().toISOString().split('T')[0],
    referenceNumber: '',
    remark:          '',
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    const paid = parseFloat(formData.paidAmount);
    if (!formData.paidAmount || paid <= 0) e.paidAmount = 'Enter valid amount';
    else if (paid > remaining) e.paidAmount = `Cannot exceed balance ₹${remaining.toFixed(2)}`;
    if (!formData.paymentMode) e.paymentMode = 'Required';
    if (!formData.paymentDate) e.paymentDate = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  return (
    <div className="pay-modal-overlay" onClick={onClose}>
      <div className="pay-modal" onClick={e => e.stopPropagation()}>
        <div className="pay-modal-header">
          <div>
            <h5 className="pay-modal-title">Add Payment</h5>
            <p className="pay-modal-subtitle">
              {payment.dealerShopName} · {payment.orderCode}
            </p>
          </div>
          <button className="pay-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="pay-modal-body">
          {/* Balance info */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:16 }}>
            {[
              { label:'Total Due',   value: fmt(totalDue),       color:'#1565c0', bg:'#e3f2fd' },
              { label:'Confirmed',   value: fmt(totalConfirmed), color:'#2e7d32', bg:'#e8f5e9' },
              { label:'Balance',     value: fmt(remaining),      color:'#f57f17', bg:'#fff8e1' },
            ].map(c => (
              <div key={c.label} style={{ background:c.bg, borderRadius:8, padding:'8px 12px', textAlign:'center' }}>
                <p style={{ margin:0, fontWeight:700, color:c.color, fontSize:14 }}>{c.value}</p>
                <p style={{ margin:0, fontSize:10, color:'#6c757d', textTransform:'uppercase' }}>{c.label}</p>
              </div>
            ))}
          </div>

          <div className="pay-row">
            <div className="pay-field">
              <label className="pay-label">Amount Received (₹) <span className="required">*</span></label>
              <input type="number" name="paidAmount" className={`pay-input ${errors.paidAmount?'error':''}`}
                value={formData.paidAmount} onChange={handleChange}
                placeholder={`Max: ${remaining.toFixed(2)}`} min="0.01" max={remaining} step="0.01"/>
              {errors.paidAmount && <span className="pay-error">{errors.paidAmount}</span>}
            </div>
            <div className="pay-field">
              <label className="pay-label">Payment Mode <span className="required">*</span></label>
              <select name="paymentMode" className={`pay-input ${errors.paymentMode?'error':''}`}
                value={formData.paymentMode} onChange={handleChange}>
                <option value="">Select Mode</option>
                {PAYMENT_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              {errors.paymentMode && <span className="pay-error">{errors.paymentMode}</span>}
            </div>
          </div>
          <div className="pay-row">
            <div className="pay-field">
              <label className="pay-label">Payment Date <span className="required">*</span></label>
              <input type="date" name="paymentDate" className={`pay-input ${errors.paymentDate?'error':''}`}
                value={formData.paymentDate} onChange={handleChange}
                max={new Date().toISOString().split('T')[0]}/>
              {errors.paymentDate && <span className="pay-error">{errors.paymentDate}</span>}
            </div>
            <div className="pay-field">
              <label className="pay-label">Reference No.</label>
              <input type="text" name="referenceNumber" className="pay-input"
                value={formData.referenceNumber} onChange={handleChange}
                placeholder="UTR / Cheque / UPI Ref"/>
            </div>
          </div>
          <div className="pay-field">
            <label className="pay-label">Remark</label>
            <textarea name="remark" className="pay-textarea" rows={2}
              value={formData.remark} onChange={handleChange} placeholder="Optional notes"/>
          </div>
        </div>
        <div className="pay-modal-footer">
          <button className="pay-btn-secondary" onClick={onClose} disabled={isSubmitting}>Cancel</button>
          <button className="pay-btn-primary" disabled={isSubmitting || remaining <= 0}
            onClick={() => { if (validate()) onSubmit(formData); }}>
            {isSubmitting ? 'Submitting...' : '→ Submit for Review'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Dealer Summary Row (expandable) ──────────────────────────────────────────
const DealerRow = ({ dealer, canAdd, canConfirm, onAddPayment, onConfirm, onReject, confirming }) => {
  const [expanded, setExpanded] = useState(false);
  const [payments, setPayments] = useState([]);
  const [loading,  setLoading]  = useState(false);

  const totalDue       = parseFloat(dealer.totalOrderAmount || 0);
  const totalConfirmed = parseFloat(dealer.totalConfirmed   || 0);
  const underReview    = parseFloat(dealer.totalUnderReview || 0);
  const balance        = totalDue - totalConfirmed;
  const pct            = totalDue > 0 ? Math.round((totalConfirmed / totalDue) * 100) : 0;

  const loadPayments = async () => {
    if (payments.length > 0) return;
    setLoading(true);
    try {
      const res = await paymentService.getPaymentsByDealerId(dealer.dealerId);
      if (res.success) setPayments(res.data || []);
    } catch { }
    finally { setLoading(false); }
  };

  const handleExpand = () => {
    if (!expanded) loadPayments();
    setExpanded(p => !p);
  };

  return (
    <>
      <tr onClick={handleExpand} style={{ cursor:'pointer', background: expanded ? '#f8f9fa' : '#fff' }}>
        <td>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:11, color:'#adb5bd', display:'inline-block',
              transform: expanded ? 'rotate(90deg)' : 'none', transition:'transform 0.2s' }}>▶</span>
            <div>
              <p style={{ margin:0, fontWeight:700, fontSize:14 }}>{dealer.dealerShopName}</p>
              <p style={{ margin:0, fontSize:11, color:'#6c757d' }}>{dealer.ownerName}</p>
            </div>
          </div>
        </td>
        <td style={{ fontWeight:700 }}>{fmt(totalDue)}</td>
        <td style={{ color:'#2e7d32', fontWeight:700 }}>{fmt(totalConfirmed)}</td>
        <td style={{ color: balance > 0 ? '#f57f17' : '#2e7d32', fontWeight:700 }}>{fmt(balance)}</td>
        {underReview > 0 && (
          <td style={{ color:'#1565c0', fontSize:12 }}>🔍 {fmt(underReview)} in review</td>
        )}
        {underReview <= 0 && <td></td>}
        <td>
          <div style={{ width:'100%', background:'#e9ecef', borderRadius:20, height:8 }}>
            <div style={{ width:`${pct}%`, background:pct===100?'#2e7d32':'#4caf50', height:'100%', borderRadius:20 }}/>
          </div>
          <span style={{ fontSize:11, color:'#6c757d' }}>{pct}%</span>
        </td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={6} style={{ padding:0, background:'#f8f9fa' }}>
            <div style={{ padding:'12px 24px 16px' }}>
              {loading ? <p style={{ color:'#adb5bd', fontSize:13 }}>Loading...</p>
              : payments.length === 0 ? <p style={{ color:'#adb5bd', fontSize:13 }}>No payments yet</p>
              : (
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                  <thead>
                    <tr style={{ background:'#e9ecef' }}>
                      <th style={{ padding:'6px 12px', textAlign:'left', fontSize:11, color:'#6c757d' }}>ORDER</th>
                      <th style={{ padding:'6px 12px', textAlign:'left', fontSize:11, color:'#6c757d' }}>TOTAL DUE</th>
                      <th style={{ padding:'6px 12px', textAlign:'left', fontSize:11, color:'#6c757d' }}>PAID</th>
                      <th style={{ padding:'6px 12px', textAlign:'left', fontSize:11, color:'#6c757d' }}>BALANCE</th>
                      <th style={{ padding:'6px 12px', textAlign:'left', fontSize:11, color:'#6c757d' }}>STATUS</th>
                      <th style={{ padding:'6px 12px', textAlign:'left', fontSize:11, color:'#6c757d' }}>RECEIVED BY</th>
                      {(canAdd || canConfirm) && <th style={{ padding:'6px 12px', fontSize:11, color:'#6c757d' }}>ACTION</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map(p => {
                      const sc  = STATUS_COLORS[p.status] || {};
                      const amt = parseFloat(p.amount || 0);
                      const conf= parseFloat(p.totalConfirmed || 0);
                      const bal = amt - conf;
                      return (
                        <tr key={p.id} style={{ borderBottom:'1px solid #e9ecef', background:'#fff' }}>
                          <td style={{ padding:'8px 12px', color:'#1565c0', fontWeight:600 }}>{p.orderCode}</td>
                          <td style={{ padding:'8px 12px', fontWeight:600 }}>{fmt(p.amount)}</td>
                          <td style={{ padding:'8px 12px', color:'#2e7d32', fontWeight:600 }}>{fmt(p.totalConfirmed)}</td>
                          <td style={{ padding:'8px 12px', color: bal > 0 ? '#f57f17':'#2e7d32', fontWeight:600 }}>{fmt(bal)}</td>
                          <td style={{ padding:'8px 12px' }}>
                            <span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:700,
                              background:sc.bg, color:sc.color }}>
                              {STATUS_LABELS[p.status] || p.status}
                            </span>
                          </td>
                          <td style={{ padding:'8px 12px' }}>
                            {p.receivedByFirstName ? `${p.receivedByFirstName} ${p.receivedByLastName}` : '—'}
                          </td>
                          {(canAdd || canConfirm) && (
                            <td style={{ padding:'8px 12px' }}>
                              <div style={{ display:'flex', gap:4 }}>
                                {canAdd && p.status === 'PENDING_PAYMENT' && bal > 0 && (
                                  <button onClick={e => { e.stopPropagation(); onAddPayment(p); }}
                                    style={{ padding:'3px 8px', background:'#e8f5e9', color:'#2e7d32', border:'1px solid #a5d6a7', borderRadius:6, fontSize:11, fontWeight:600, cursor:'pointer' }}>
                                    + Pay
                                  </button>
                                )}
                                {canConfirm && p.status === 'UNDER_REVIEW' && (
                                  <>
                                    <button onClick={e => { e.stopPropagation(); onConfirm(p.id); }}
                                      disabled={confirming===p.id}
                                      style={{ padding:'3px 8px', background:'#4caf50', color:'#fff', border:'none', borderRadius:6, fontSize:11, fontWeight:600, cursor:'pointer' }}>
                                      {confirming===p.id?'...':'✓'}
                                    </button>
                                    <button onClick={e => { e.stopPropagation(); onReject(p.id); }}
                                      disabled={confirming===p.id}
                                      style={{ padding:'3px 8px', background:'#fce4ec', color:'#c62828', border:'none', borderRadius:6, fontSize:11, fontWeight:600, cursor:'pointer' }}>
                                      ✕
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const PaymentsPage = () => {
  const { user }   = useAuth();
  const canAdd     = CAN_ADD_ROLES.includes(user?.role);
  const canConfirm = CAN_CONFIRM_ROLES.includes(user?.role);

  const [activeTab,   setActiveTab]   = useState('PENDING_PAYMENT');
  const [payments,    setPayments]    = useState([]);
  const [dealers,     setDealers]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [pagination,  setPagination]  = useState({ page:1, limit:20, total:0, totalPages:0 });
  const [toast,       setToast]       = useState(null);
  const [isSubmitting,setIsSubmitting]= useState(false);
  const [confirming,  setConfirming]  = useState(null);

  // Modals
  const [addPaymentModal,  setAddPaymentModal]  = useState(null); // payment record
  const [confirmModal,     setConfirmModal]     = useState({ open:false, id:null });
  const [rejectModal,      setRejectModal]      = useState({ open:false, id:null });
  const [rejectNote,       setRejectNote]       = useState('');

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'DEALER') {
        const res = await paymentService.getPaymentsByDealer();
        if (res.success) setDealers(res.data || []);
      } else {
        const res = await paymentService.getPayments(pagination.page, pagination.limit, search, activeTab);
        if (res.success) {
          setPayments(res.data.payments || []);
          setPagination(prev => ({ ...prev, ...res.data.pagination }));
        }
      }
    } catch (err) {
      console.error('Fetch payments error:', err);
      setToast({ message: 'Failed to fetch payments', type: 'error' });
    } finally { setLoading(false); }
  }, [activeTab, pagination.page, search]);

  useEffect(() => { fetchPayments(); }, [activeTab, pagination.page, search]);

  const handleAddPaymentSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      const res = await paymentService.addPayment(formData);
      if (res.success) {
        setToast({ message: 'Payment submitted for review!', type: 'success' });
        setAddPaymentModal(null);
        fetchPayments();
      }
    } catch (err) {
      console.error('Add payment error:', err);
      setToast({ message: err.response?.data?.message || 'Failed to add payment', type: 'error' });
    } finally { setIsSubmitting(false); }
  };

  const handleConfirmSubmit = async () => {
    const id = confirmModal.id;
    setConfirmModal({ open:false, id:null });
    setConfirming(id);
    try {
      const res = await paymentService.confirmPayment(id);
      setToast({ message: res.message || 'Payment confirmed!', type: 'success' });
      fetchPayments();
    } catch (err) {
      console.error('Confirm error:', err);
      setToast({ message: err.response?.data?.message || 'Failed to confirm', type: 'error' });
    } finally { setConfirming(null); }
  };

  const handleRejectSubmit = async () => {
    const id = rejectModal.id;
    setRejectModal({ open:false, id:null });
    setConfirming(id);
    try {
      await paymentService.rejectPayment(id, rejectNote);
      setToast({ message: 'Payment rejected. Balance restored.', type: 'success' });
      setRejectNote('');
      fetchPayments();
    } catch (err) {
      console.error('Reject error:', err);
      setToast({ message: err.response?.data?.message || 'Failed to reject', type: 'error' });
    } finally { setConfirming(null); }
  };

  // Summary counts
  const counts = {
    PENDING_PAYMENT: payments.filter(p => p.status === 'PENDING_PAYMENT').length,
    UNDER_REVIEW:    payments.filter(p => p.status === 'UNDER_REVIEW').length,
    CONFIRMED:       payments.filter(p => p.status === 'CONFIRMED').length,
    REJECTED:        payments.filter(p => p.status === 'REJECTED').length,
  };

  return (
    <div className="pay-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.open}
        title="Confirm Payment Received"
        message="Are you sure you have received this payment? This will update the dealer's balance."
        confirmLabel="✓ Confirm Received"
        confirmType="success"
        onConfirm={handleConfirmSubmit}
        onClose={() => setConfirmModal({ open:false, id:null })}
      />

      {/* Reject Modal */}
      <ConfirmModal
        isOpen={rejectModal.open}
        title="Reject Payment"
        message="This payment will be rejected and the balance will be restored."
        confirmLabel="✕ Reject"
        confirmType="danger"
        onConfirm={handleRejectSubmit}
        onClose={() => { setRejectModal({ open:false, id:null }); setRejectNote(''); }}
      >
        <label style={{ fontSize:12, fontWeight:600, color:'#495057', display:'block', marginBottom:6 }}>
          Reason (optional)
        </label>
        <textarea rows={2} style={{ width:'100%', padding:'8px 12px', border:'1px solid #dee2e6', borderRadius:8, fontSize:13, resize:'vertical', boxSizing:'border-box' }}
          value={rejectNote} onChange={e => setRejectNote(e.target.value)}
          placeholder="Why are you rejecting this payment?"/>
      </ConfirmModal>

      <nav className="pay-breadcrumb">
        <a href="/dashboard/overview">Dashboard</a><span>›</span>
        <a href="/orders/all">Orders</a><span>›</span><span>Payments</span>
      </nav>

      <div className="pay-header">
        <div>
          <h4 className="pay-title">Payments</h4>
          <p className="pay-subtitle">Track dealer payments across all delivered orders</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="order-tabs" style={{ marginBottom:16 }}>
        {TABS.map(tab => (
          <button key={tab.key}
            className={`order-tab ${activeTab===tab.key?'active':''}`}
            onClick={() => { setActiveTab(tab.key); setPagination(prev => ({ ...prev, page:1 })); }}>
            {tab.label}
            {tab.key !== 'DEALER' && counts[tab.key] > 0 && (
              <span className="order-tab-count">{counts[tab.key]}</span>
            )}
          </button>
        ))}
      </div>

      {/* Search — flat tabs only */}
      {activeTab !== 'DEALER' && (
        <div className="pay-search-bar" style={{ marginBottom:16 }}>
          <input type="text" className="pay-search-input"
            placeholder="Search dealer, order ID..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPagination(prev => ({ ...prev, page:1 })); }}/>
          <span>🔍</span>
        </div>
      )}

      {/* ── DEALER TAB ── */}
      {activeTab === 'DEALER' && (
        <div className="pay-table-container">
          {loading ? <div className="pay-loading">Loading...</div>
          : dealers.length === 0 ? <div className="pay-empty"><p>No dealer payments found</p></div>
          : (
            <table className="pay-table">
              <thead>
                <tr>
                  <th>Dealer</th><th>Total Due</th><th>Confirmed</th>
                  <th>Balance</th><th>Under Review</th><th>Progress</th>
                </tr>
              </thead>
              <tbody>
                {dealers.map(dealer => (
                  <DealerRow key={dealer.dealerId} dealer={dealer}
                    canAdd={canAdd} canConfirm={canConfirm}
                    onAddPayment={setAddPaymentModal}
                    onConfirm={(id) => setConfirmModal({ open:true, id })}
                    onReject={(id) => setRejectModal({ open:true, id })}
                    confirming={confirming}/>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── FLAT TABS ── */}
      {activeTab !== 'DEALER' && (
        <div className="pay-table-container">
          {loading ? <div className="pay-loading">Loading...</div>
          : payments.length === 0 ? (
            <div className="pay-empty">
              <p>{activeTab === 'PENDING_PAYMENT' ? '🎉 No pending payments!' : '💳 No payments found'}</p>
              {activeTab === 'PENDING_PAYMENT' && <p style={{ fontSize:13, color:'#6c757d' }}>All delivered orders have been paid</p>}
            </div>
          ) : (
            <table className="pay-table">
              <thead>
                <tr>
                  <th>#</th><th>Dealer</th><th>Order</th>
                  <th>Total Due</th><th>Confirmed</th><th>Balance</th>
                  {activeTab === 'UNDER_REVIEW' && <th>Under Review</th>}
                  <th>Mode</th><th>Date</th><th>Received By</th><th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p, idx) => {
                  const sc   = STATUS_COLORS[p.status] || {};
                  const amt  = parseFloat(p.amount || 0);
                  const conf = parseFloat(p.totalConfirmed || 0);
                  const bal  = amt - conf;
                  return (
                    <tr key={p.id}>
                      <td>{(pagination.page-1)*pagination.limit+idx+1}</td>
                      <td style={{ fontWeight:600 }}>{p.dealerShopName || '—'}</td>
                      <td style={{ fontSize:12, color:'#1565c0' }}>{p.orderCode || '—'}</td>
                      <td style={{ fontWeight:700 }}>{fmt(amt)}</td>
                      <td style={{ color:'#2e7d32', fontWeight:600 }}>{fmt(conf)}</td>
                      <td style={{ color: bal > 0 ? '#f57f17':'#2e7d32', fontWeight:700 }}>{fmt(bal)}</td>
                      {activeTab === 'UNDER_REVIEW' && (
                        <td style={{ color:'#1565c0', fontWeight:600 }}>{fmt(p.paidAmount)}</td>
                      )}
                      <td style={{ fontSize:12 }}>{modeLabel(p.paymentMode)}</td>
                      <td style={{ fontSize:12 }}>{fmtDt(p.paymentDate)}</td>
                      <td style={{ fontSize:13 }}>
                        {p.receivedByFirstName ? `${p.receivedByFirstName} ${p.receivedByLastName}` : '—'}
                      </td>
                      <td>
                        <span className="pay-status-badge" style={{ background:sc.bg, color:sc.color }}>
                          {STATUS_LABELS[p.status] || p.status}
                        </span>
                        {p.paymentNote && p.status === 'PENDING_PAYMENT' && (
                          <p style={{ fontSize:10, color:'#c62828', margin:'2px 0 0 0' }}>
                            ⚠ {p.paymentNote}
                          </p>
                        )}
                      </td>
                      <td>
                        <div style={{ display:'flex', gap:5 }}>
                          {canAdd && p.status === 'PENDING_PAYMENT' && bal > 0 && (
                            <button onClick={() => setAddPaymentModal({ ...p, orderId: p.rawOrderId })}
                              style={{ padding:'4px 10px', background:'#e8f5e9', color:'#2e7d32', border:'1px solid #a5d6a7', borderRadius:6, fontSize:12, fontWeight:600, cursor:'pointer' }}>
                              + Pay
                            </button>
                          )}
                          {canConfirm && p.status === 'UNDER_REVIEW' && (
                            <>
                              <button onClick={() => setConfirmModal({ open:true, id:p.id })}
                                disabled={confirming===p.id}
                                style={{ padding:'4px 10px', background:'#4caf50', color:'#fff', border:'none', borderRadius:6, fontSize:12, fontWeight:600, cursor:'pointer' }}>
                                {confirming===p.id?'...':'✓ Confirm'}
                              </button>
                              <button onClick={() => setRejectModal({ open:true, id:p.id })}
                                disabled={confirming===p.id}
                                style={{ padding:'4px 10px', background:'#fce4ec', color:'#c62828', border:'none', borderRadius:6, fontSize:12, fontWeight:600, cursor:'pointer' }}>
                                ✕ Reject
                              </button>
                            </>
                          )}
                          {!canAdd && !canConfirm && <span style={{ color:'#adb5bd', fontSize:12 }}>—</span>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Pagination */}
      {activeTab !== 'DEALER' && pagination.totalPages > 1 && (
        <div className="pay-pagination">
          <button className="pay-page-btn" disabled={pagination.page===1}
            onClick={() => setPagination(prev => ({ ...prev, page:prev.page-1 }))}>← Previous</button>
          <span className="pay-page-info">Page {pagination.page} of {pagination.totalPages}</span>
          <button className="pay-page-btn" disabled={pagination.page>=pagination.totalPages}
            onClick={() => setPagination(prev => ({ ...prev, page:prev.page+1 }))}>Next →</button>
        </div>
      )}

      {/* Add Payment Modal */}
      {addPaymentModal && (
        <AddPaymentModal
          payment={addPaymentModal}
          onClose={() => setAddPaymentModal(null)}
          onSubmit={handleAddPaymentSubmit}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
};

export default PaymentsPage;